<template>
  <div v-if="isOpen" class="dialog-overlay" @click="handleCancel()">
    <div class="dialog-content" @click.stop>
      <div class="dialog-header">
        <h2>Delete Training Sessions</h2>
        <button class="close-btn" @click="handleCancel()">✕</button>
      </div>

      <div class="dialog-body">
        <div class="section">
          <p class="warning-text">Are you sure you want to move the following training sessions to recycle bin?</p>
        <h3>Selected Sessions to Delete</h3>
          
          <div class="sessions-list">
            <div v-for="session in selectedSessions" :key="session.id" class="session-item">
              <div class="session-id">{{ toHex(session.id) }}</div>
              <div class="session-name">{{ session.name || 'Untitled' }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="dialog-footer">
        <button class="dialog-button dialog-button-cancel" @click="handleCancel()">
          Cancel
        </button>
        <button
          class="dialog-button dialog-button-delete"
          :disabled="selectedSessions.length === 0"
          @click="handleDelete()"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const $toast = useToast();

const props = defineProps({
  selectedSessions: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['cancelled', 'delete-sessions']);

const isOpen = ref(false);

const toHex = (id) => {
  return "t-" + id.toString(16);
};

const openModal = () => {
  isOpen.value = true;
};

const closeModal = () => {
  isOpen.value = false;
};

const handleCancel = () => {
  closeModal();
  emit('cancelled');
};

const handleDelete = async () => {
  if (props.selectedSessions.length === 0) {
    $toast.open({
      queue: true,
      dismissible: true,
      message: 'No sessions selected to delete',
      type: 'warning',
      position: 'top',
    });
    return;
  }
  
  try {
    const dataConnect = new DatastoreConnect();
    const sessionIds = props.selectedSessions.map(session => session.id);
    
    await dataConnect.deleteTrainingSession({
      session_ids: sessionIds,
    });

    emit('delete-sessions', {
      sessions: props.selectedSessions,
      success: true,
    });
    
    $toast.open({
      queue: true,
      dismissible: true,
      message: `Successfully deleted ${props.selectedSessions.length} session(s)`,
      type: 'success',
      position: 'top',
    });
    
    closeModal();
  } catch (error) {
    const errorMessage = error?.message || 'Error deleting sessions';
    console.error('Error deleting sessions:', error);
    $toast.open({
      queue: true,
      dismissible: true,
      message: errorMessage,
      type: 'error',
      position: 'top',
    });
  }
};

defineExpose({
  openModal,
  closeModal,
});
</script>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';

.section {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }

  h3 {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 600;
  }
}

.warning-text {
  margin: 0 0 12px 0;
  font-size: 15px;
  color: #d31901;
  line-height: 1.5;
}

.sessions-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
}

.session-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  background: #f9f9f9;
  border-radius: 4px;
  font-size: 13px;

  .session-id {
    font-weight: 600;
    color: #666;
    min-width: 80px;
  }

  .session-name {
    flex: 1;
    color: #333;
  }
}

</style>
